import { useCallback, useEffect, useRef, useState } from "react";

export type ClockPhase = "idle" | "countdown" | "running" | "finished";

/** Seconds of "3... 2... 1..." before a timed run's clock starts. */
export const COUNTDOWN_MS = 3000;
const TICK_MS = 250;

export interface QuizClock {
    phase: ClockPhase;
    /** 3, 2 or 1 while `phase` is "countdown"; 0 otherwise. */
    countdownSeconds: number;
    /** Milliseconds left in the run while `phase` is "running"; 0 otherwise. */
    remainingMs: number;
    start(): void;
    stop(): void;
}

/**
 * The run clock.
 *
 * A timed run gets a 3-2-1 countdown so the timer is not already draining while
 * the child reads the first problem - otherwise the first question is
 * systematically the slowest and the score punishes reading speed. An untimed
 * run (`durationMs` null) goes straight to "running" and shows no clock at all;
 * it ends only when the caller calls `stop()`.
 *
 * Time comes from `Date.now()` deltas, not accumulated ticks. Intervals drift,
 * and a background tab throttles them, so a run that survives a tab switch must
 * not silently gain time - the tick only forces a re-render, the wall clock is
 * the source of truth.
 */
export function useQuizClock(durationMs: number | null): QuizClock {
    const [phase, setPhase] = useState<ClockPhase>("idle");
    const [nowTick, setNowTick] = useState(0);
    const countdownStartedAt = useRef(0);
    const runStartedAt = useRef(0);
    const timer = useRef<ReturnType<typeof setInterval>>();

    const clearTimer = useCallback(() => {
        if (timer.current) {
            clearInterval(timer.current);
            timer.current = undefined;
        }
    }, []);

    const stop = useCallback(() => {
        clearTimer();
        setPhase("finished");
    }, [clearTimer]);

    const start = useCallback(() => {
        clearTimer();
        if (durationMs === null) {
            setPhase("running");
            return;
        }
        countdownStartedAt.current = Date.now();
        setNowTick(Date.now());
        setPhase("countdown");
        timer.current = setInterval(() => setNowTick(Date.now()), TICK_MS);
    }, [durationMs, clearTimer]);

    useEffect(() => clearTimer, [clearTimer]);

    // Phase changes are read off the wall clock, not fired from timer callbacks.
    useEffect(() => {
        if (durationMs === null) {
            return;
        }
        if (phase === "countdown" && Date.now() - countdownStartedAt.current >= COUNTDOWN_MS) {
            runStartedAt.current = countdownStartedAt.current + COUNTDOWN_MS;
            setPhase("running");
        } else if (phase === "running" && Date.now() - runStartedAt.current >= durationMs) {
            stop();
        }
    }, [nowTick, phase, durationMs, stop]);

    let countdownSeconds = 0;
    let remainingMs = 0;
    if (phase === "countdown") {
        countdownSeconds = Math.max(
            1,
            Math.ceil((COUNTDOWN_MS - (Date.now() - countdownStartedAt.current)) / 1000)
        );
    } else if (phase === "running" && durationMs !== null) {
        remainingMs = Math.max(0, durationMs - (Date.now() - runStartedAt.current));
    }

    return { phase, countdownSeconds, remainingMs, start, stop };
}
